import { sendErrorReport } from "./api";

/**
 * Zentrale Stelle für alles, was im Frontend unerwartet kaputtgeht: Der
 * Fehler landet in der Browser-Konsole **und** beim Backend, wo er in der
 * Container-Konsole und als Datei im Log-Ordner ankommt.
 */

/**
 * Obergrenze pro Sitzung. Eine Komponente, die bei jedem Rendern wirft,
 * erzeugt sonst einen endlosen Strom von Meldungen. Das Backend begrenzt
 * ebenfalls, aber der Absender soll gar nicht erst losrennen.
 */
const MAX_MELDUNGEN_PRO_SITZUNG = 10;

let gesendet = 0;
export type FrontendErrorReport = {
	readonly message: string;
	readonly stack: string | null;
	readonly origin: string;
	readonly url: string;
	readonly userAgent: string;
	readonly timestamp: string;
};

export function buildFrontendErrorReport(args: {
	thrown: unknown;
	origin: string;
	url: string;
	userAgent: string;
	timestamp: Date;
}): FrontendErrorReport {
	const thrown = args.thrown;

	return {
		message: thrown instanceof Error ? thrown.message : String(thrown),
		stack: thrown instanceof Error ? (thrown.stack ?? null) : null,
		origin: args.origin,
		url: args.url,
		userAgent: args.userAgent,
		timestamp: args.timestamp.toISOString(),
	};
}

export function reportError(args: { thrown: unknown; origin: string }): void {
	const report = buildFrontendErrorReport({
		thrown: args.thrown,
		origin: args.origin,
		url: window.location.href,
		userAgent: navigator.userAgent,
		timestamp: new Date(),
	});

	console.error(`[frontend-error] ${report.origin}: ${report.message}`, report);

	if (gesendet >= MAX_MELDUNGEN_PRO_SITZUNG) {
		return;
	}

	gesendet += 1;

	// Bewusst nicht abgewartet und bewusst verschluckt: Scheitert das Melden,
	// darf daraus kein neuer Fehler entstehen, den wir wieder melden würden.
	void sendErrorReport(report).catch((ursache: unknown) => {
		console.error("[frontend-error] konnte nicht an das Backend gemeldet werden", ursache);
	});
}

/** Fängt ab, was an React vorbeifliegt: lose Promises und globale Fehler. */
export function registerGlobalErrorHandlers(args: { target: Window }): void {
	args.target.addEventListener("error", (event: ErrorEvent) => {
		reportError({ thrown: event.error ?? event.message, origin: "window.error" });
	});

	args.target.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
		reportError({ thrown: event.reason, origin: "unhandledrejection" });
	});
}
